import React from 'react'

import {
  DateValue,
  Button as AriaDatepickerButton,
  Calendar as AriaDatepickerCalendar,
  CalendarCell as AriaDatepickerCalendarCell,
  CalendarGrid as AriaDatepickerCalendarGrid,
  DateInput as AriaDatepickerDateInput,
  DatePicker as AriaDatepicker,
  DateSegment as AriaDatepickerDateSegment,
  Dialog as AriaDatepickerDialog,
  Group as AriaDatepickerGroup,
  Heading as AriaDatepickerHeading,
  Label as AriaDatepickerLabel,
  Popover as AriaDatepickerPopover
} from 'react-aria-components'

import {
  DatepickerRootProps,
  DatepickerLabelProps,
  DatepickerButtonProps,
  DatepickerCalendarProps,
  DatepickerGroupProps,
  DatepickerDateInputProps,
  DatepickerDateSegmentProps,
  DatepickerPopoverProps,
  DatepickerDialogProps,
  DatepickerHeadingProps,
  DatepickerCalendarCellProps,
  DatepickerCalendarGridProps
} from '.'

/**
 * A date picker combines a DateField and a Calendar popover to allow users to enter or select a date and time value.
 */
const Root = <T extends DateValue>({children, ...props}: DatepickerRootProps<T>) => {
  return <AriaDatepicker {...props}>{children}</AriaDatepicker>
}
/** A label provides context for an input element. */
const Label = ({children, ...props}: DatepickerLabelProps) => {
  return <AriaDatepickerLabel {...props}>{children}</AriaDatepickerLabel>
}
/** A button allows a user to perform an action. */
const Button = ({children, ...props}: DatepickerButtonProps) => {
  return <AriaDatepickerButton {...props}>{children}</AriaDatepickerButton>
}
/** A calendar allows a user to select a single date from a date grid. */
const Calendar = <T extends DateValue>({children, ...props}: DatepickerCalendarProps<T>) => {
  return <AriaDatepickerCalendar {...props}>{children}</AriaDatepickerCalendar>
}
/**
 * An group represents a set of related UI controls.
 */
const Group = ({children, ...props}: DatepickerGroupProps) => {
  return <AriaDatepickerGroup {...props}>{children}</AriaDatepickerGroup>
}

const DateInput = ({...props}: DatepickerDateInputProps) => {
  return <AriaDatepickerDateInput {...props} />
}
/**
 * A date segment displays an individual unit of a date and time, and allows users to edit
 * the value by typing or using the arrow keys to increment and decrement.
 */
const DateSegment = ({...props}: DatepickerDateSegmentProps) => {
  return <AriaDatepickerDateSegment {...props} />
}
/** A popover displays content in context with a trigger element */
const Popover = ({...props}: DatepickerPopoverProps) => {
  return <AriaDatepickerPopover {...props} />
}
/** A dialog is an overlay shown above other content in an application */
const Dialog = ({...props}: DatepickerDialogProps) => {
  return <AriaDatepickerDialog {...props} />
}
/** Displays a heading */
const Heading = ({...props}: DatepickerHeadingProps) => {
  return <AriaDatepickerHeading {...props} />
}
/** A CalendarCell renders an individual date within a CalendarGrid. */
const CalendarCell = ({...props}: DatepickerCalendarCellProps) => {
  return <AriaDatepickerCalendarCell {...props} />
}
/** A CalendarGrid renders an individual month within a Calendar. It accepts a function as its children, which is called to render a CalendarCell for each date.  */
const CalendarGrid = ({...props}: DatepickerCalendarGridProps) => {
  return <AriaDatepickerCalendarGrid {...props} />
}

// exports
Root.displayName = 'Datepicker'
Button.displayName = 'Button'
Calendar.displayName = 'Calendar'
Label.displayName = 'Label'
Group.displayName = 'Group'
DateInput.displayName = 'DateInput'
DateSegment.displayName = 'DateSegment'
Popover.displayName = 'Popover'
Dialog.displayName = 'Dialog'
Heading.displayName = 'Heading'
CalendarCell.displayName = 'CalendarCell'
CalendarGrid.displayName = 'CalendarGrid'

/**
 * A date picker combines a DateField and a Calendar popover to allow users to enter or select a date and time value.
 */
export default Object.assign(Root, {
  Button,
  Calendar,
  Label,
  Group,
  DateInput,
  DateSegment,
  Popover,
  Dialog,
  Heading,
  CalendarCell,
  CalendarGrid
})
